/**
 * まなび(5教科クイズ)の問題ブラウザ。
 *
 * カリキュラム → 帯(ようじ / しょうがく1ねん …)→ 教科 → 単元 → 問題一覧。
 * 帯はカリキュラムパック(`learn/{id}/pack.json`。docs/13 §9)から検出するので、
 * 学年が増えても画面はそのまま追随する。
 * **カリキュラムのセレクタはパックが2つ以上あるときだけ出す**
 * (1つしか無いあいだ「選べるのに選択肢が1つ」の枠を見せない)。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  bandLabel,
  filterQuestions,
  skillCounts,
  unitsOfSubject,
  type BandContent,
  type BandRef,
  type PackRef,
  type QuizLoader,
} from '../services/quiz';
import type { RequestTarget } from './RequestDialog';
import { QuizTable } from './QuizTable';

interface Props {
  readonly loader: QuizLoader;
  readonly assetsBase: string | null;
  readonly onRequest: (target: RequestTarget) => void;
}

/** 修正メモの対象ラベル(問題文。長すぎるとルール側で弾かれるので後で切られる)。 */
const LABEL_LIMIT = 80;

export function QuizBrowser({ loader, assetsBase, onRequest }: Props) {
  const [packs, setPacks] = useState<readonly PackRef[]>([]);
  const [curriculum, setCurriculum] = useState<string | undefined>(undefined);
  const [bands, setBands] = useState<readonly BandRef[] | null>(null);
  const [band, setBand] = useState<BandRef | null>(null);
  const [content, setContent] = useState<BandContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [unit, setUnit] = useState('');
  const [query, setQuery] = useState('');

  // 配信されているカリキュラムパックの一覧(manifest 由来)。
  useEffect(() => {
    let cancelled = false;
    loader
      .listPacks()
      .then((list) => {
        if (!cancelled) setPacks(list);
      })
      .catch(() => {
        // パックが読めなくても帯の一覧は従来の経路で出せる(セレクタを出さないだけ)。
        if (!cancelled) setPacks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [loader]);

  // 帯の一覧(選んだカリキュラムのパック)。最初の帯を既定で開く。
  useEffect(() => {
    let cancelled = false;
    loader
      .listBands(curriculum)
      .then((list) => {
        if (cancelled) return;
        setBands(list);
        // カリキュラムを切り替えたら、いまの帯が無いこともある。
        setBand((current) => list.find((ref) => ref.band === current?.band) ?? list[0] ?? null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [loader, curriculum]);

  // 選んだ帯の問題集。
  useEffect(() => {
    if (band === null) return undefined;
    let cancelled = false;
    setContent(null);
    setError(null);
    loader
      .load(band.band, band.path)
      .then((loaded) => {
        if (cancelled) return;
        setContent(loaded);
        // 帯が変われば教科・単元の選択は意味を失う。
        setSubject('');
        setUnit('');
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [loader, band]);

  const units = useMemo(
    () =>
      content === null ? [] : subject === '' ? content.units : unitsOfSubject(content, subject),
    [content, subject],
  );

  const questions = useMemo(
    () => (content === null ? [] : filterQuestions(content, { subject, unit, query })),
    [content, subject, unit, query],
  );

  const skills = useMemo(() => skillCounts(questions), [questions]);

  if (error !== null) {
    return (
      <div className="workspace">
        <div className="pane pane--full">
          <div className="notice notice--error" role="alert">
            まなびの問題を読み込めませんでした: {error}
          </div>
        </div>
      </div>
    );
  }

  if (bands === null || content === null || band === null) {
    return (
      <div className="workspace">
        <div className="pane pane--full">
          <p className="muted">まなびの問題を読み込み中…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace">
      <div className="pane pane--full">
        {/* カリキュラム(パックが2つ以上あるときだけ) */}
        {packs.length > 1 && (
          <div className="toolbar">
            <span className="field__label" style={{ margin: 0 }}>
              カリキュラム
            </span>
            <select
              className="select"
              style={{ maxWidth: '220px' }}
              value={curriculum ?? packs[0]?.curriculum ?? ''}
              onChange={(event) => {
                setCurriculum(event.target.value);
              }}
            >
              {packs.map((pack) => (
                <option key={pack.curriculum} value={pack.curriculum}>
                  {pack.curriculum}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 帯 */}
        <div className="toolbar">
          <span className="field__label" style={{ margin: 0 }}>
            帯
          </span>
          <div className="tabs">
            {bands.map((candidate) => (
              <button
                key={candidate.band}
                type="button"
                className={`tab${candidate.band === band.band ? ' tab--active' : ''}`}
                onClick={() => {
                  setBand(candidate);
                }}
              >
                {candidate.label ?? bandLabel(candidate.band)}
                <span className="muted mono"> {candidate.band}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 教科 */}
        <div className="toolbar">
          <span className="field__label" style={{ margin: 0 }}>
            教科
          </span>
          <div className="tabs">
            <button
              type="button"
              className={`tab${subject === '' ? ' tab--active' : ''}`}
              onClick={() => {
                setSubject('');
                setUnit('');
              }}
            >
              すべて
            </button>
            {content.subjects.map((name) => (
              <button
                key={name}
                type="button"
                className={`tab${name === subject ? ' tab--active' : ''}`}
                onClick={() => {
                  setSubject(name);
                  setUnit('');
                }}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {/* 単元・検索 */}
        <div className="toolbar">
          <select
            className="select"
            style={{ maxWidth: '280px' }}
            value={unit}
            onChange={(event) => {
              setUnit(event.target.value);
            }}
          >
            <option value="">単元で絞り込む(すべて)</option>
            {units.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.subject} / {candidate.title}({String(candidate.questionIds.length)})
              </option>
            ))}
          </select>
          <input
            className="input"
            style={{ maxWidth: '320px' }}
            type="search"
            placeholder="問題文・選択肢・解説・skills・ID で検索"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <span className="muted mono">
            {String(questions.length)} 問表示 / 全 {String(content.questions.length)} 問(
            {String(content.units.length)} 単元)
          </span>
        </div>

        {content.missingQuestionIds.length > 0 && (
          <div className="notice notice--error" role="alert">
            参照切れの問題ID({String(content.missingQuestionIds.length)}件):{' '}
            <span className="mono">{content.missingQuestionIds.join(', ')}</span>
          </div>
        )}
        {content.orphanQuestionIds.length > 0 && (
          <div className="notice">
            どの単元にも入っていない問題({String(content.orphanQuestionIds.length)}件):{' '}
            <span className="mono">{content.orphanQuestionIds.join(', ')}</span>
          </div>
        )}
        {band.count > 0 && band.count !== content.questions.length && (
          <div className="notice notice--error" role="alert">
            manifest の問題数({String(band.count)})と実データ(
            {String(content.questions.length)})が食い違っています。
          </div>
        )}

        {skills.length > 0 && (
          <p>
            {skills.slice(0, 20).map(([name, count]) => (
              <span className="tag" key={name}>
                {name}({String(count)})
              </span>
            ))}
          </p>
        )}

        <QuizTable
          questions={questions}
          content={content}
          assetsBase={assetsBase}
          onRequest={(question) => {
            onRequest({
              type: 'quiz',
              id: question.id,
              label: question.prompt.text.slice(0, LABEL_LIMIT),
              language: 'japanese',
              band: question.band,
              defaultKind: 'quiz-prompt',
            });
          }}
        />
      </div>
    </div>
  );
}
